import { useLocalParticipant, useRoomContext, useSpeakingParticipants } from "@livekit/components-react";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ArrowDown } from "lucide-react";
import { useTranslate } from '@tolgee/react';
import ParticipantComponent from "./ParticipantComponent";
import { calculateGalleryColumns } from "../../Helpers/callHelper/galleryColumnsHelper";
import { useElementSize } from "../../Helpers/callHelper/useElementSize";
import Pill from "../GenericComponents/Pill";
import { colors, rgb } from "../../Assets/styles/colors";

const ParticipantsWrapper = ({
  callInfo,
  participants,
  grid,
  speakersQueue,
}) => {

  const room = useRoomContext();
  const localParticipant = useLocalParticipant();
  const activeSpeakers = useSpeakingParticipants();
  const { t } = useTranslate();
  const [speakingParticipant, setSpeakingParticipant] = useState(
    localParticipant.localParticipant
  );
  const speakerSelectedManually = useRef(false);

  const { ref: galleryRef, width: galleryWidth, height: galleryHeight } = useElementSize();
  const columns = calculateGalleryColumns(participants.length, galleryWidth, galleryHeight);

  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const updateHasMoreBelow = () => {
    const el = galleryRef.current;
    if (!el) {
      setHasMoreBelow(false);
      return;
    }
    const remainingPx = el.scrollHeight - el.scrollTop - el.clientHeight;
    setHasMoreBelow(remainingPx > 4);
  };

  useEffect(() => {
    updateHasMoreBelow();
  }, [participants, columns, galleryWidth, galleryHeight]);

  const handleParticipantSpeaking = (participant) => {
    participant.on("isSpeakingChanged", () => {
      if (participant.isSpeaking && speakerSelectedManually.current === false) {
        setSpeakingParticipant(participant);
      }
    });
  };

  const focusParticipant = (participant) => {
    speakerSelectedManually.current = true;
    setSpeakingParticipant(participant);
  }

  useEffect(() => {
    if (grid && activeSpeakers.length > 0) {
      if (speakerSelectedManually.current === false) {
        
        const loudestSpeakerIdentity = activeSpeakers[0]?.identity;

        const matchedParticipant = participants.find(
          (p) => p.identity === loudestSpeakerIdentity
        );

        if (matchedParticipant && speakingParticipant?.identity !== matchedParticipant.identity) {
             setSpeakingParticipant(matchedParticipant);
        }
      }
    }
  }, [activeSpeakers, grid, speakingParticipant, participants]);

  return (
    <Wrapper>
      {!grid && (
        <GridSection>
          <GridViewContainer ref={galleryRef} columns={columns} onScroll={updateHasMoreBelow}>
            {participants.map((participant, index) => (
              <ParticipantComponent
                participant={participant}
                callInfo={callInfo}
                key={`${participant.identity}-${index}`}
                speakersQueue={speakersQueue}
                compact
              />
            ))}
          </GridViewContainer>
          {hasMoreBelow && (
            <Pill
              size="md"
              color={colors.speakingGreen}
              $borderColor={`rgba(${rgb(colors.speakingGreen)}, 0.4)`}
              $bottom={12}
              $centerX
            >
              {t('call_modal.scroll_for_more')}
              <ArrowDown size={14} />
            </Pill>
          )}
        </GridSection>
      )}

      {grid && (
        <FocusViewContainer>
          <ParticipantsListWrapper>
            {participants.map((participant) => (
              <ParticipantComponent
                participant={participant}
                callInfo={callInfo}
                key={participant.identity}
                grid={true}
                focusParticipant={focusParticipant}
                speakersQueue={speakersQueue}
              />
            ))}
          </ParticipantsListWrapper>
          <FocusWrapper>
            <ParticipantComponent
              participant={speakingParticipant}
              callInfo={callInfo}
              key={speakingParticipant.identity}
              grid={false}
              focused={true}
              speakersQueue={speakersQueue}
            />
          </FocusWrapper>
        </FocusViewContainer>
      )}
    </Wrapper>
  );
};

export default ParticipantsWrapper;
const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const GridSection = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
const GridViewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  grid-auto-rows: 1fr;
  width: 100%;
  height: 100%;
  gap: 10px;
  max-height: 100%;
  overflow: auto;
`;
const FocusViewContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  gap: 20px;
  padding: 5px;
`;
const ParticipantsListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  width: 100%;
  min-width: 200px;
  max-width: 250px;
  gap: 10px;
`;
const FocusWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
